import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ClosePreviewResponse } from '@/api/handlers/resources/periods/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { PeriodQueryKeys } from './periodQueryKeys';

/**
 * Dry run of the year-end close.
 *
 * Modelled as a **query** because that is what the preview dialog wants — it
 * opens, fetches, and gets loading / error / retry for free — and because a dry
 * run writes nothing. `enabled` keeps it from firing until the dialog is open.
 * It is invalidated by `invalidateAfterPeriodWrite`, so committing a close makes
 * its own preview stale rather than leaving a phantom on screen.
 *
 * The request carries no body; `locale` is its only parameter, and it is
 * measured to change the account names the server resolves.
 *
 * ## There is no sibling
 *
 * This file used to export `useRolloverPreviewQuery` too.
 * `/ledger/fiscal-years/{year}/rollover/preview` **does not exist** — 404 on
 * both verbs — so there is no rollover dry run to hang a hook on. The rollover
 * action sits behind `LedgerHandoffNotice` for exactly that reason: its commit
 * endpoint is real, but approving a real post with no preview is the hazard the
 * notice exists to prevent.
 */
export const useFiscalYearClosePreviewQuery = (
    fiscalYearId: string | null,
    enabled: boolean,
    locale?: string,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<ClosePreviewResponse>>({
        queryKey: PeriodQueryKeys.closePreview(fiscalYearId ?? '', locale),
        queryFn: () =>
            apiClient.periods.previewFiscalYearClose(fiscalYearId!, locale),
        enabled: enabled && fiscalYearId !== null,
        // A dry run is cheap and must reflect the ledger as it is right now.
        staleTime: 0,
        gcTime: 0,
    });
};
